import {
  AuthBroadcastType,
  XEMPLO_AUTH_ACTIVE_SUB_KEY,
  XEMPLO_AUTH_CHANNEL,
  XEMPLO_AUTH_LOGOUT_KEY,
} from '@xemplo/cross-app-events';
import { safeCall } from '@xemplo/safe-call';

let channel: BroadcastChannel | null = null;

/**
 * Lazily creates the singleton cross-app auth BroadcastChannel. A single shared
 * instance matters: BroadcastChannel never delivers to the poster, so the tab that
 * logs out won't react to its own broadcast. Returns null where unavailable (SSR).
 */
export function getAuthChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(XEMPLO_AUTH_CHANNEL);
  return channel;
}

/** Tell other open tabs/stacks (hr-fe, other React apps) to tear down their session. */
export function broadcastLogout(): void {
  getAuthChannel()?.postMessage({ type: AuthBroadcastType.Logout });
}

/**
 * Tell other open tabs/stacks that a new identity just signed in. A tab whose
 * cached user has a different `sub` re-establishes auth — this is what catches
 * same-tab user switching that a logout-only broadcast misses.
 */
export function broadcastLogin(sub: string): void {
  getAuthChannel()?.postMessage({ type: AuthBroadcastType.Login, sub });
}

/**
 * Persist the time of this logout so an app loading later in the same tab (after
 * navigating away and back) can detect it — the durable backstop to the broadcast.
 */
export function markLoggedOut(): void {
  safeCall(() => localStorage.setItem(XEMPLO_AUTH_LOGOUT_KEY, Date.now().toString()));
}

/** Epoch-ms timestamp of the last cross-app logout, or null if none / unavailable. */
export function getLogoutAt(): number | null {
  const [value] = safeCall(() => localStorage.getItem(XEMPLO_AUTH_LOGOUT_KEY));
  return value ? Number(value) : null;
}

/**
 * Drop the logout marker once a fresh sign-in succeeds. The marker is written with
 * the client clock and compared against the server's `auth_time`; clearing it on
 * login closes the clock-skew window (a re-auth reading as older than the marker
 * would otherwise re-logout) and stops the value lingering indefinitely.
 */
export function clearLogoutMarker(): void {
  safeCall(() => localStorage.removeItem(XEMPLO_AUTH_LOGOUT_KEY));
}

/** Record the identity (id_token `sub`) currently authenticated on this origin. */
export function setActiveSub(sub: string): void {
  safeCall(() => localStorage.setItem(XEMPLO_AUTH_ACTIVE_SUB_KEY, sub));
}

/** The `sub` of the identity last signed in on this origin, or null if none / unavailable. */
export function getActiveSub(): string | null {
  const [value] = safeCall(() => localStorage.getItem(XEMPLO_AUTH_ACTIVE_SUB_KEY));
  return value ?? null;
}

/** Drop the active-identity marker on logout so a later load reads "no active user". */
export function clearActiveSub(): void {
  safeCall(() => localStorage.removeItem(XEMPLO_AUTH_ACTIVE_SUB_KEY));
}
